import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { AppStore, ThemeMode, UserPreferences, ExportFormat } from '../state/app.store';

export interface StoredUserPreferences {
  userKey: string;
  themeMode: ThemeMode;
  recentSearches: string[];
  moduleSearches: Record<string, string>;
  tablePageSize: number;
  compactView: boolean;
  defaultExportFormat: ExportFormat;
  autoRefreshEnabled: boolean;
  activeBenchmarkDimension: 'Regions' | 'Countries' | 'Companies';
  lastSelectedRegion: string | null;
  lastSelectedCountry: string | null;
  lastSelectedCompanyCode: string | null;
  lastUpdated: number;
}

const STORAGE_PREFIX = 'gars_user_prefs_';
const GLOBAL_BACKUP_KEY = 'gars_user_preferences';
const ACTIVE_USER_KEY = 'gars_active_user_key';
const KNOWN_USERS_KEY = 'gars_known_pref_users';

const DEFAULT_PREFERENCES: Omit<StoredUserPreferences, 'userKey' | 'lastUpdated'> = {
  themeMode: 'light',
  recentSearches: [],
  moduleSearches: {},
  tablePageSize: 10,
  compactView: false,
  defaultExportFormat: 'excel',
  autoRefreshEnabled: true,
  activeBenchmarkDimension: 'Regions',
  lastSelectedRegion: null,
  lastSelectedCountry: null,
  lastSelectedCompanyCode: null,
};

@Injectable({ providedIn: 'root' })
export class PreferencesService {
  // Active user identifier (e.g. 'vishwas.doijode.ext@holcim.com')
  readonly currentUserKey = signal<string>(this.resolveInitialUserKey());

  // Full reactive preferences object for active user
  readonly preferences = signal<StoredUserPreferences>(this.loadPreferencesForUser(this.currentUserKey()));

  // Convenient direct signals - Light mode standard
  readonly themeMode = computed<ThemeMode>(() => 'light');
  readonly isDarkMode = computed<boolean>(() => false);
  readonly recentSearches = computed<string[]>(() => this.preferences().recentSearches);
  readonly tablePageSize = computed<number>(() => this.preferences().tablePageSize);
  readonly compactView = computed<boolean>(() => this.preferences().compactView);
  readonly defaultExportFormat = computed<ExportFormat>(() => this.preferences().defaultExportFormat);

  constructor() {
    // Enforce standard light theme on boot
    this.applyThemeToDom('light');
  }

  // ══════════════════════════════════════════════════════════════════
  // USER IDENTIFIER & LIFECYCLE
  // ══════════════════════════════════════════════════════════════════

  private normalizeKey(raw: string | null | undefined): string {
    if (!raw) return 'default';
    const trimmed = raw.trim().toLowerCase();
    return trimmed.replace(/[^a-z0-9@._-]/g, '_') || 'default';
  }

  private resolveInitialUserKey(): string {
    try {
      // 1. Try reading logged-in user from localStorage
      const userRaw = localStorage.getItem('user');
      if (userRaw) {
        const parsed = JSON.parse(userRaw);
        const email = parsed?.email || parsed?.userPrincipalName || parsed?.userName;
        if (email) return this.normalizeKey(email);
      }
      // 2. Try last active user key
      const active = localStorage.getItem(ACTIVE_USER_KEY);
      if (active) return this.normalizeKey(active);
    } catch {
      // ignore
    }
    return 'default';
  }

  /**
   * Sets or switches the active user context.
   * Restores that specific user's preferences without erasing anyone else's.
   */
  setUser(userIdentifier: string | null | undefined): void {
    const key = this.normalizeKey(userIdentifier);
    if (key === this.currentUserKey()) return;

    this.currentUserKey.set(key);
    try {
      localStorage.setItem(ACTIVE_USER_KEY, key);
      this.trackKnownUser(key);
    } catch { /* noop */ }

    // Load or initialize preferences for this specific user
    const userPrefs = this.loadPreferencesForUser(key);
    this.preferences.set(userPrefs);
    this.applyThemeToDom(userPrefs.themeMode);
  }

  private trackKnownUser(key: string): void {
    try {
      const raw = localStorage.getItem(KNOWN_USERS_KEY);
      const list: string[] = raw ? JSON.parse(raw) : [];
      if (!list.includes(key)) {
        list.push(key);
        localStorage.setItem(KNOWN_USERS_KEY, JSON.stringify(list));
      }
    } catch { /* noop */ }
  }

  // ══════════════════════════════════════════════════════════════════
  // PERSISTENCE (Never cleared on logout)
  // ══════════════════════════════════════════════════════════════════

  private loadPreferencesForUser(userKey: string): StoredUserPreferences {
    try {
      // 1. Check dedicated user storage key
      const storageKey = STORAGE_PREFIX + userKey;
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          ...DEFAULT_PREFERENCES,
          ...parsed,
          userKey,
          recentSearches: Array.isArray(parsed.recentSearches) ? parsed.recentSearches : [],
          moduleSearches: parsed.moduleSearches && typeof parsed.moduleSearches === 'object' ? parsed.moduleSearches : {},
        };
      }

      // 2. Fall back to global backup key if userKey is 'default' or newly created
      const globalRaw = localStorage.getItem(GLOBAL_BACKUP_KEY);
      if (globalRaw) {
        const parsed = JSON.parse(globalRaw);
        return {
          ...DEFAULT_PREFERENCES,
          ...parsed,
          userKey,
          recentSearches: Array.isArray(parsed.recentSearches) ? parsed.recentSearches : [],
          moduleSearches: parsed.moduleSearches && typeof parsed.moduleSearches === 'object' ? parsed.moduleSearches : {},
        };
      }
    } catch {
      // ignore
    }

    return {
      ...DEFAULT_PREFERENCES,
      userKey,
      lastUpdated: Date.now(),
    };
  }

  private persist(updated: StoredUserPreferences): void {
    const key = updated.userKey || this.currentUserKey();
    const data = {
      ...updated,
      userKey: key,
      lastUpdated: Date.now(),
    };

    try {
      // 1. Save user-specific entry
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(data));
      // 2. Save active backup
      localStorage.setItem(GLOBAL_BACKUP_KEY, JSON.stringify(data));
      localStorage.setItem(ACTIVE_USER_KEY, key);
      this.trackKnownUser(key);
    } catch (e) {
      console.warn('Could not persist user preferences to localStorage:', e);
    }

    this.preferences.set(data);
  }

  // ══════════════════════════════════════════════════════════════════
  // THEME MANAGEMENT (Light / Dark / System)
  // ══════════════════════════════════════════════════════════════════

  /**
   * Toggles directly between Light and Dark mode.
   */
  toggleTheme(): void {
    // Light mode standard - theme toggling disabled
    this.applyThemeToDom('light');
  }

  setThemeMode(mode: ThemeMode): void {
    // Light mode standard
    const updated: StoredUserPreferences = {
      ...this.preferences(),
      themeMode: 'light',
    };
    this.persist(updated);
    this.applyThemeToDom('light');
  }

  private applyThemeToDom(_mode: ThemeMode = 'light'): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const root = document.documentElement;
    const body = document.body;

    root.classList.remove('dark');
    root.classList.remove('app-dark');
    body.classList.remove('dark-mode');
    root.setAttribute('data-theme', 'light');
    root.style.colorScheme = 'light';
  }

  // ══════════════════════════════════════════════════════════════════
  // SEARCH HISTORY & CACHING
  // ══════════════════════════════════════════════════════════════════

  /**
   * Records a search query into recent searches and optionally module cache.
   * Deduplicates, keeps latest at top, and limits to 25 items.
   */
  addSearchQuery(query: string, module?: string): void {
    if (!query) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) return;

    const currentSearches = this.preferences().recentSearches.filter(
      (s) => s.toLowerCase() !== trimmed.toLowerCase()
    );
    const updatedSearches = [trimmed, ...currentSearches].slice(0, 25);

    const updatedModuleSearches = { ...this.preferences().moduleSearches };
    if (module) {
      updatedModuleSearches[module] = trimmed;
    }

    this.persist({
      ...this.preferences(),
      recentSearches: updatedSearches,
      moduleSearches: updatedModuleSearches,
    });
  }

  /**
   * Removes a single query from recent searches.
   */
  removeSearchQuery(query: string): void {
    const trimmed = query.trim().toLowerCase();
    const filtered = this.preferences().recentSearches.filter(
      (s) => s.toLowerCase() !== trimmed
    );
    this.persist({
      ...this.preferences(),
      recentSearches: filtered,
    });
  }

  /**
   * Clears all cached recent searches for current user.
   */
  clearSearchHistory(): void {
    this.persist({
      ...this.preferences(),
      recentSearches: [],
    });
  }

  /**
   * Gets last cached search string for a module (e.g. 'settings', 'gemini-reports').
   */
  getModuleSearch(module: string): string {
    return this.preferences().moduleSearches?.[module] || '';
  }

  /**
   * Sets cached search string for a specific module and records it into history.
   */
  setModuleSearch(module: string, query: string): void {
    this.addSearchQuery(query, module);
  }

  // ══════════════════════════════════════════════════════════════════
  // TABLE & VIEW PREFERENCES
  // ══════════════════════════════════════════════════════════════════

  setTablePageSize(size: number): void {
    if (this.preferences().tablePageSize === size) return;
    this.persist({
      ...this.preferences(),
      tablePageSize: size,
    });
  }

  setCompactView(compact: boolean): void {
    if (this.preferences().compactView === compact) return;
    this.persist({
      ...this.preferences(),
      compactView: compact,
    });
  }

  setDefaultExportFormat(format: ExportFormat): void {
    if (this.preferences().defaultExportFormat === format) return;
    this.persist({
      ...this.preferences(),
      defaultExportFormat: format,
    });
  }

  updatePreferences(partial: Partial<StoredUserPreferences>): void {
    this.persist({
      ...this.preferences(),
      ...partial,
    });
  }
}
